/*
 * Legible runtime colors (WoW class, parse, role, blessing).
 *
 * Those colors arrive as hex authored for a dark background, and several vanish
 * on the light palette. `tc()` darkens one just enough to read on light and is
 * identity on dark.
 *
 * WHICH PALETTE: not the site-wide light/dark choice, but the palette the
 * component is actually rendered inside. A shared component can sit on a
 * themed page and on a page that is still dark-only; the second must not have
 * its colors darkened. Themed panels declare their palette through
 * PaletteContext (ThemedPage, the planner shell); anywhere else it is "dark".
 *
 * USAGE: call useLegible() once at the top of a page's root component, then
 * wrap colors with tc(). The page root sets it on every render, before its
 * children render, so children can call tc() without their own hook.
 */
import { createContext, useContext } from "react";
import { onLight } from "./tokens";

export const PaletteContext = createContext<"calm" | "dark">("dark");

const identity = (hex: string) => hex;
let current: (hex: string) => string = identity;
let palette: "calm" | "dark" = "dark";

export function useLegible(): void {
  palette = useContext(PaletteContext);
  current = palette === "calm" ? onLight : identity;
}

/*
 * Style for a native <option>. Options render in an OS popup that does not
 * inherit page styles, so each carries a concrete colour: a CSS variable is
 * not reliable there. Values mirror --c-surface / --c-ink in themes.css.
 */
export function optionStyle(): { background: string; color: string } {
  return palette === "calm"
    ? { background: "#f8f3eb", color: "#453a31" }
    : { background: "#0D1014", color: "#E7EDF5" };
}

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/* Only plain hex is adjusted; var(), rgba() and names pass through untouched. */
export function tc<T extends string | null | undefined>(color: T): T {
  return (color && HEX.test(color) ? current(color) : color) as T;
}
